/**
 * "Someone changed something" — the signal that lets a screen stop polling.
 *
 * A poll asks the database "did anything change?" on a timer, which means it
 * asks hundreds of times for every time the answer is yes. But the browser
 * already knows when the answer is yes: every change to this data starts as a
 * write from this app. So the write announces itself, and the screens that care
 * refresh on the announcement instead of on a clock.
 *
 * The announcement is raised in two places at once:
 *
 *   - `window` CustomEvent — reaches other components in the same tab.
 *   - a `localStorage` key — the browser fires a `storage` event in the OTHER
 *     tabs of the same browser (never in the tab that wrote it), which is how a
 *     change made in a second tab reaches the first without asking the server.
 *
 * What this deliberately does NOT cover is a change made on a DIFFERENT device.
 * Screens that need that keep a cheap catch-up of their own — a refresh when the
 * tab is looked at again — rather than a standing timer. Where correctness (not
 * just freshness) depends on being current, the server is the authority and
 * refuses a stale write; the UI is not the safety mechanism.
 *
 * Signals are coalesced by `onDataChanged`, so a bulk operation that fires fifty
 * writes causes one refresh, not fifty.
 */

const EVENT_NAME = 'app:data-changed';
const STORAGE_KEY = 'app:data-changed';

/**
 * Announce that a write succeeded. Called from the HTTP client, so it covers
 * every write the app makes — including changes the server records as a side
 * effect of some other request, which no hand-placed call site would catch.
 *
 * @param {string} [source] endpoint or short label, for debugging only
 */
export function signalDataChanged(source = '') {
  const detail = { source, at: Date.now() };

  try {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail }));
  } catch (err) {
    // A dispatch failure must never fail the write that triggered it.
  }

  try {
    // The value has to differ each time or some browsers skip the event.
    localStorage.setItem(STORAGE_KEY, JSON.stringify(detail));
  } catch (err) {
    // Private mode / storage disabled: same-tab listeners still work.
  }
}

/**
 * Subscribe to writes made anywhere in this browser.
 *
 * The handler must only READ. A handler that writes announces itself, which
 * calls the handler again — the one way to turn this into a loop.
 *
 * @param {Function} handler        called after the burst settles
 * @param {object}   [options]
 * @param {number}   [options.debounceMs=800] how long to wait for the burst to end
 * @returns {Function} unsubscribe
 */
export function onDataChanged(handler, options = {}) {
  const { debounceMs = 800 } = options;
  let timer = null;

  const fire = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      handler();
    }, debounceMs);
  };

  const onStorage = (event) => {
    if (event.key === STORAGE_KEY) fire();
  };

  window.addEventListener(EVENT_NAME, fire);
  window.addEventListener('storage', onStorage);

  return () => {
    if (timer) clearTimeout(timer);
    window.removeEventListener(EVENT_NAME, fire);
    window.removeEventListener('storage', onStorage);
  };
}

/**
 * Run `handler` when the tab is looked at again.
 *
 * This is the catch-up for a change made on another device, which no in-browser
 * signal can know about. It costs one query at the moment someone actually
 * returns to the screen, instead of one every N seconds forever.
 *
 * @param {Function} handler
 * @param {object}   [options]
 * @param {number}   [options.minGapMs=5000] ignore a return that follows the last one closely
 * @returns {Function} unsubscribe
 */
export function onScreenResumed(handler, options = {}) {
  const { minGapMs = 5000 } = options;
  let lastRunAt = 0;

  const onVisibility = () => {
    if (document.visibilityState !== 'visible') return;
    const now = Date.now();
    if (now - lastRunAt < minGapMs) return;
    lastRunAt = now;
    handler();
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('focus', onVisibility);

  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('focus', onVisibility);
  };
}
